import { useEffect, useState } from "react";
import { DollarSign, Download, Inbox, Loader2, LogOut, MailCheck, Package, RefreshCw, ShieldCheck, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const STATUS_STYLES = {
  paid: "text-green-400 border-green-400/30",
  pending: "text-amber-400 border-amber-400/30",
  error: "text-red-400 border-red-400/30",
};

export default function AdminPage() {
  useSEO("Shop Owner Admin — The Mechanic's Tool Kit", "Admin area for The Mechanic's Tool Kit.");
  const [user, setUser] = useState(undefined);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const loadAll = async () => {
    try {
      const [msgs, ords, lds, rvs, docs] = await Promise.all([
        api.get("/admin/messages"),
        api.get("/admin/orders"),
        api.get("/admin/leads"),
        api.get("/admin/reviews"),
        api.get("/admin/documents"),
      ]);
      setMessages(msgs.data);
      setOrders(ords.data);
      setLeads(lds.data);
      setReviews(rvs.data);
      setDocuments(docs.data);
    } catch {
      toast.error("Could not load admin data");
    }
  };

  const toggleReview = async (id) => {
    try {
      await api.post(`/admin/reviews/${id}/approve`);
      loadAll();
    } catch {
      toast.error("Could not update the review");
    }
  };

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setMessages([]);
    setOrders([]);
  };

  const fieldCls = "w-full bg-raised border border-slate-400/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-blaze/60 transition-colors";

  if (user === undefined) {
    return <main className="pt-40 pb-24 min-h-screen text-center"><Loader2 className="w-8 h-8 text-blaze animate-spin mx-auto" /></main>;
  }

  const paid = orders.filter((o) => o.status === "paid");
  const revenue = paid.reduce((s, o) => s + o.amount, 0) / 100;
  const pendingCount = orders.filter((o) => o.status === "pending").length;

  const STATS = [
    { icon: DollarSign, label: "Revenue collected", value: `$${revenue.toLocaleString("en-US", { minimumFractionDigits: 2 })}` },
    { icon: Package, label: "Paid orders", value: paid.length },
    { icon: Timer, label: "Pending checkouts", value: pendingCount },
    { icon: Inbox, label: "Open messages", value: messages.length },
  ];

  return (
    <main className="pt-28 pb-24 min-h-screen blueprint-grid" data-testid="admin-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {!user ? (
          <div className="max-w-md mx-auto clip-notch border border-slate-400/15 bg-panel p-8">
            <ShieldCheck className="w-8 h-8 text-blaze" />
            <h1 className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-slate-50">Shop Owner Login</h1>
            <p className="mt-2 text-sm text-slate-400">Orders and message inbox for the shop desk.</p>
            <form onSubmit={login} className="mt-7 space-y-4">
              <input data-testid="admin-email-input" type="email" required placeholder="admin email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls} />
              <input data-testid="admin-password-input" type="password" required placeholder="password"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={fieldCls} />
              {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
              <button data-testid="admin-login-button" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-blaze text-white font-display font-bold uppercase tracking-wide text-base px-6 py-4 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Open the dashboard"}
              </button>
            </form>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-blaze font-semibold">Admin // Shop Desk</p>
                <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50">Sales dashboard</h1>
              </div>
              <div className="flex gap-3">
                <button data-testid="admin-refresh-button" onClick={loadAll}
                  className="inline-flex items-center gap-2 border border-slate-400/25 text-slate-300 font-mono text-[11px] uppercase tracking-[0.16em] px-4 py-2.5 hover:border-blaze/60 hover:text-blaze transition-colors">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
                <button data-testid="admin-logout-button" onClick={logout}
                  className="inline-flex items-center gap-2 border border-slate-400/25 text-slate-300 font-mono text-[11px] uppercase tracking-[0.16em] px-4 py-2.5 hover:border-red-400/60 hover:text-red-400 transition-colors">
                  <LogOut className="w-4 h-4" /> Log out
                </button>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 border border-slate-400/10 divide-x divide-y lg:divide-y-0 divide-slate-400/10 bg-panel/60">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="p-5">
                  <s.icon className="w-4 h-4 text-blaze" />
                  <div className="mt-2 font-mono font-bold tracking-tighter text-2xl text-slate-50">{s.value}</div>
                  <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">Document library — all 14 files</h2>
              <a data-testid="admin-docs-download-all" href={`${API}/admin/documents-download-all`}
                className="inline-flex items-center gap-2 border border-blaze/50 text-blaze font-mono text-[10px] uppercase tracking-[0.16em] px-4 py-2 hover:bg-blaze hover:text-white transition-colors">
                <Download className="w-3.5 h-3.5" /> Download all (.zip)
              </a>
            </div>
            <div data-testid="admin-docs-list" className="mt-4 border border-slate-400/10 bg-panel divide-y divide-slate-400/5">
              {documents.map((d) => (
                <div key={d.doc_id} className="flex items-center justify-between gap-3 px-6 py-3.5 hover:bg-raised/60 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100 truncate">{d.title}</p>
                    <p className="font-mono text-[9px] text-slate-500 mt-0.5 truncate">
                      {d.filename} · {d.tiers.length === 3 ? "all kits" : d.tiers.length === 2 ? "Builder + Complete" : "Complete only"}
                    </p>
                  </div>
                  <a data-testid={`admin-doc-download-${d.doc_id}`} href={`${API}/admin/documents/${d.doc_id}/download`}
                    className="shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400 hover:text-blaze transition-colors">
                    <Download className="w-4 h-4" /> PDF
                  </a>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-slate-100">Orders</h2>
            <div data-testid="admin-orders-list" className="mt-4 border border-slate-400/10 bg-panel divide-y divide-slate-400/5">
              {orders.length === 0 && (
                <div className="p-10 text-center">
                  <Package className="w-8 h-8 text-slate-600 mx-auto" />
                  <p className="mt-3 text-sm text-slate-500">No orders yet. Share the link and watch this board light up.</p>
                </div>
              )}
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100 font-semibold truncate">{o.kit_name}</p>
                    <p className="font-mono text-[10px] text-slate-500 mt-0.5">
                      {new Date(o.created_at).toLocaleString()}{o.buyer_email ? ` · ${o.buyer_email}` : ""}{o.download_count ? ` · ${o.download_count} download${o.download_count === 1 ? "" : "s"}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {o.status === "paid" && o.receipt_sent && (
                      <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">
                        <MailCheck className="w-3.5 h-3.5 text-green-400" /> receipt sent
                      </span>
                    )}
                    <span className="font-mono text-sm font-bold text-slate-100">${(o.amount / 100).toFixed(2)}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.16em] border px-2 py-1 ${STATUS_STYLES[o.status] || "text-slate-400 border-slate-400/30"}`}>
                      {o.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-slate-100">Contact messages</h2>
            <div data-testid="admin-messages-list" className="mt-4 space-y-4">
              {messages.length === 0 && (
                <div className="border border-slate-400/10 bg-panel p-10 text-center">
                  <Inbox className="w-8 h-8 text-slate-600 mx-auto" />
                  <p className="mt-3 text-sm text-slate-500">No messages yet. The inbox is quiet — go turn some wrenches.</p>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className="border border-slate-400/10 bg-panel p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-slate-100 font-semibold">{m.name} <span className="font-mono text-xs text-blaze font-normal">&lt;{m.email}&gt;</span></p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-slate-100">Free sample leads</h2>
            <div data-testid="admin-leads-list" className="mt-4 border border-slate-400/10 bg-panel divide-y divide-slate-400/5">
              {leads.length === 0 && (
                <p className="p-6 text-sm text-slate-500">No leads yet — share the /free page in mechanic groups to start collecting emails.</p>
              )}
              {leads.map((l) => (
                <div key={l.id || l.email} data-testid={`admin-lead-${l.email}`} className="flex flex-wrap items-center justify-between gap-2 px-6 py-4">
                  <p className="text-sm text-slate-100">{l.name} <span className="font-mono text-xs text-blaze">&lt;{l.email}&gt;</span></p>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{new Date(l.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-slate-100">Reviews — approve to publish</h2>
            <div data-testid="admin-reviews-list" className="mt-4 space-y-4">
              {reviews.length === 0 && (
                <p className="border border-slate-400/10 bg-panel p-6 text-sm text-slate-500">No reviews yet. They land here after someone claims the free sample.</p>
              )}
              {reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-slate-400/10 bg-panel p-6 flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-100 font-semibold">
                      {r.name}{r.shop ? ` — ${r.shop}` : ""} <span className="text-blaze font-mono text-xs">{"★".repeat(r.rating)}</span>
                    </p>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">"{r.text}"</p>
                  </div>
                  <button
                    data-testid={`admin-review-approve-${r.id}`}
                    onClick={() => toggleReview(r.id)}
                    className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] border px-3 py-2 transition-colors ${
                      r.approved
                        ? "border-green-400/40 text-green-400 hover:border-red-400/60 hover:text-red-400"
                        : "border-slate-400/25 text-slate-300 hover:border-green-400/60 hover:text-green-400"
                    }`}
                  >
                    {r.approved ? "Live — click to unpublish" : "Approve & publish"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
